import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, isPlaceholder } from "@/lib/content";
import { useSite } from "@/lib/SiteContext";

const linkCls = ({ isActive }) =>
  `focus-ring whitespace-nowrap rounded-md px-1 py-2 text-sm font-medium transition-colors hover:text-teal-600 ${
    isActive ? "text-teal-600" : "text-slate-700"
  }`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useSite();
  const phone = settings.phone;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 md:h-[72px] lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-4 lg:flex xl:gap-5" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.to.includes("#") ? (
              <Link key={l.to} to={l.to} className="focus-ring whitespace-nowrap rounded-md px-1 py-2 text-sm font-medium text-slate-700 transition-colors hover:text-teal-600" data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </Link>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="flex items-center gap-2">
          {!isPlaceholder(phone) ? (
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="focus-ring hidden items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-navy transition-colors hover:text-teal-600 md:flex lg:hidden xl:flex" data-testid="header-call-button">
              <Phone className="h-4 w-4 text-teal-600" /> {phone}
            </a>
          ) : (
            <Link to="/contact" className="focus-ring hidden items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold text-navy transition-colors hover:text-teal-600 md:flex lg:hidden xl:flex" data-testid="header-call-button">
              <Phone className="h-4 w-4 text-teal-600" /> Call Us
            </Link>
          )}
          <Link to="/quote" className="focus-ring hidden whitespace-nowrap rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-md sm:block" data-testid="header-quote-button">
            Get a Free Quote
          </Link>
          <button type="button" onClick={() => setOpen(!open)} className="focus-ring rounded-md p-2 text-navy lg:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-slate-200 bg-white lg:hidden" aria-label="Mobile">
          <div className="mx-auto flex max-w-7xl flex-col px-4 py-4">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="focus-ring rounded-lg px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-50" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/quote" onClick={() => setOpen(false)} className="focus-ring mt-3 rounded-full bg-teal-600 px-5 py-3 text-center text-sm font-semibold text-white" data-testid="mobile-menu-quote-button">
              Get a Free Quote
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
